import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import Layout from '../components/Layout'
import { api } from '../lib/api'
import { useAlert } from '../components/AlertProvider'

interface PublicProfile {
  id: number
  user_id: number
  username: string | null
  display_name: string | null
  bio: string | null
  picture: string | null
  birth_year: number | null
  age?: number | null
  age_range?: string | null
  gender: 'male' | 'female' | 'other' | null
  country: string | null
  city: string | null
  mbti: string | null
  interests: string[] | null
  languages: string[] | null
  has_persona?: number
  current_stage?: number
  stage_info?: {
    current: number
    next_unlocks: string[]
  }
  photos?: any[]
  like_count?: number
  created_at: string
  updated_at: string
}

export default function PublicProfilePage() {
  const { t } = useTranslation()
  const { userId } = useParams()
  const navigate = useNavigate()
  const { user, profile: currentUserProfile } = useStore()
  const { showAlert } = useAlert()

  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [liking, setLiking] = useState(false)
  const [liked, setLiked] = useState(false)

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true)
        setError(null)

        if (!userId) {
          setError(t('publicProfile.noUserId'))
          return
        }

        const id = parseInt(userId)
        if (isNaN(id)) {
          setError(t('publicProfile.invalidUserId'))
          return
        }

        // 자신의 프로필 조회 시 프로필 페이지로 리다이렉트
        if (user?.id === id) {
          navigate('/profile')
          return
        }

        // 현재 유저 프로필을 보여줍니다 (임시: 실제로는 다른 사용자 정보를 조회해야 함)
        if (currentUserProfile && currentUserProfile.id === id) {
          setProfile({
            id: currentUserProfile.id || 0,
            user_id: id,
            username: (currentUserProfile as any).username || null,
            display_name: currentUserProfile.display_name || null,
            bio: currentUserProfile.bio || null,
            picture: (currentUserProfile as any).picture || null,
            birth_year: currentUserProfile.birth_year || null,
            gender: currentUserProfile.gender || null,
            country: currentUserProfile.country || null,
            city: currentUserProfile.city || null,
            mbti: currentUserProfile.mbti || null,
            interests: currentUserProfile.interests || null,
            languages: currentUserProfile.languages || null,
            created_at: currentUserProfile.created_at || '',
            updated_at: currentUserProfile.updated_at || '',
          })
        } else {
          // 다른 사용자 프로필 조회
          const data = await api.users.getUserProfile(id)
          console.log('[PublicProfile] Loaded profile data:', data)
          setProfile({
            id: 0, // 프로필 ID는 중요하지 않음
            user_id: data.user_id,
            username: data.username,
            display_name: data.display_name,
            bio: data.bio,
            picture: data.picture,
            birth_year: data.birth_year,
            age: data.age,
            age_range: data.age_range,
            gender: data.gender,
            country: data.country,
            city: data.city,
            mbti: data.mbti,
            interests: data.interests,
            languages: data.languages,
            has_persona: data.has_persona,
            current_stage: data.current_stage,
            stage_info: data.stage_info,
            photos: data.photos,
            like_count: data.like_count,
            created_at: data.created_at,
            updated_at: data.updated_at,
          })

          // API 응답에서 좋아요 상태 설정
          setLiked(data.liked || false)
        }
      } catch (err) {
        console.error('Failed to load profile:', err)
        setError(t('publicProfile.loadFailed'))
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [userId, user?.id, navigate, t])

  const handleLike = async () => {
    if (!profile || !user || liking || !userId) return

    const targetUserId = parseInt(userId)
    if (isNaN(targetUserId)) return

    setLiking(true)
    try {
      if (liked) {
        // 좋아요 취소
        await api.users.unlike(targetUserId)
        setLiked(false)
        // 좋아요 개수 감소
        setProfile(prev => prev ? { ...prev, like_count: Math.max(0, (prev.like_count || 0) - 1) } : null)
      } else {
        // 좋아요 추가
        await api.users.like(targetUserId)
        setLiked(true)
        // 좋아요 개수 증가
        setProfile(prev => prev ? { ...prev, like_count: (prev.like_count || 0) + 1 } : null)
      }
    } catch (err) {
      console.error('Failed to toggle like:', err)
      showAlert(t('publicProfile.likeFailed'))
    } finally {
      setLiking(false)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </Layout>
    )
  }

  if (error || !profile) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <div className="alert alert-error">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l-2-2m0 0l-2-2m2 2l2-2m-2 2l-2 2m2-2l2 2m-2-2l-2-2" />
            </svg>
            <span>{error || t('publicProfile.notFound')}</span>
          </div>
          <div className="mt-4">
            <button onClick={() => navigate(-1)} className="btn btn-primary">
              {t('publicProfile.backButton')}
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  const currentStage = profile.current_stage || 0
  const stageInfo = profile.stage_info || { current: 0, next_unlocks: [] }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {/* Stage Progress Indicator */}
        {currentStage > 0 && (
          <div className="alert bg-gradient-to-r from-primary/10 to-secondary/10 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge badge-primary">Stage {currentStage}</span>
                <span className="text-sm font-semibold">
                  {currentStage === 1 && '상상의 단계'}
                  {currentStage === 2 && '청각의 단계'}
                  {currentStage === 3 && '시각의 단계'}
                </span>
              </div>
              {stageInfo.next_unlocks && stageInfo.next_unlocks.length > 0 && (
                <p className="text-xs text-base-content/70">
                  다음 단계 공개 정보: {stageInfo.next_unlocks.join(', ')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* 프로필 헤더 카드 */}
        <div className="card bg-base-100 shadow-lg overflow-hidden">
          {/* 배경 이미지 - Stage 3 이상에서만 표시 */}
          {currentStage >= 3 && profile.picture && (
            <div className="relative w-full h-64 bg-gradient-to-br from-coral-100 to-mint-100">
              <img
                src={profile.picture}
                alt={profile.display_name || ''}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          )}
          {/* Stage 3 미만일 때 블러 처리된 플레이스홀더 */}
          {currentStage < 3 && (
            <div className="relative w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
              <div className="text-center">
                <span className="text-6xl">🔒</span>
                <p className="text-sm text-base-content/60 mt-2">
                  Stage 3에서 프로필 사진이 공개됩니다
                </p>
              </div>
            </div>
          )}

          <div className="card-body">
            {/* 프로필 정보 */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold">
                    {currentStage >= 1
                      ? (profile.display_name || profile.username || t('publicProfile.unknown'))
                      : '???'}
                  </h1>
                  {currentStage >= 1 && profile.mbti && (
                    <span className="badge badge-lg badge-primary">{profile.mbti}</span>
                  )}
                  {profile.has_persona === 1 && (
                    <span
                      className="text-sm opacity-60"
                      title="AI 자동응답 활성화됨"
                    >
                      🤖
                    </span>
                  )}
                </div>

                {currentStage >= 1 && (
                  <p className="text-base-content/70 mt-2">
                    {profile.country && `${profile.country}`}
                    {currentStage >= 2 && profile.city && ` · ${profile.city}`}
                  </p>
                )}

                {currentStage >= 1 && profile.bio && (
                  <p className="text-base-content/80 mt-3 leading-relaxed">{profile.bio}</p>
                )}

                {currentStage < 1 && (
                  <p className="text-sm text-base-content/50 italic mt-2">
                    대화를 시작하면 상대방의 정보가 공개됩니다
                  </p>
                )}
              </div>

              {/* 좋아요 버튼 */}
              {user?.id !== profile.user_id && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleLike}
                    disabled={liking}
                    className={`btn btn-lg rounded-full gap-2 ${liked ? 'btn-primary' : 'btn-outline'
                      }`}
                  >
                    {liking ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      <>
                        <span className="text-xl">💕</span>
                        <span>{liked ? t('publicProfile.unlike') : t('publicProfile.like')}</span>
                      </>
                    )}
                  </button>
                  {profile.like_count !== undefined && profile.like_count > 0 && (
                    <span className="text-sm text-gray-500">
                      {profile.like_count}명이 좋아합니다
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* 관심사 - Stage 1+ */}
            {currentStage >= 1 && Array.isArray(profile.interests) && profile.interests.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-sm mb-2">{t('publicProfile.interests')}</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, idx) => (
                    <span key={idx} className="badge badge-lg badge-ghost">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 언어 - Stage 2+ */}
            {currentStage >= 2 && Array.isArray(profile.languages) && profile.languages.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold text-sm mb-2">{t('publicProfile.languages')}</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang, idx) => (
                    <span key={idx} className="badge badge-outline">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 추가 정보 */}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
              {/* Stage 2: 나이대 표시 */}
              {currentStage >= 2 && currentStage < 3 && profile.age_range && (
                <div>
                  <p className="text-sm text-base-content/60">{t('publicProfile.age')}</p>
                  <p className="font-semibold">{profile.age_range}</p>
                </div>
              )}
              {/* Stage 3: 정확한 나이 표시 */}
              {currentStage >= 3 && profile.age && (
                <div>
                  <p className="text-sm text-base-content/60">{t('publicProfile.age')}</p>
                  <p className="font-semibold">{profile.age}{t('publicProfile.ageUnit')}</p>
                </div>
              )}
              {currentStage >= 2 && profile.gender && (
                <div>
                  <p className="text-sm text-base-content/60">{t('publicProfile.gender')}</p>
                  <p className="font-semibold">
                    {profile.gender === 'male' ? t('publicProfile.male') : profile.gender === 'female' ? t('publicProfile.female') : t('publicProfile.other')}
                  </p>
                </div>
              )}
            </div>

            {/* Photos Gallery (Stage 3) */}
            {currentStage >= 3 && profile.photos && profile.photos.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-sm mb-2">프로필 사진</h3>
                <div className="grid grid-cols-3 gap-2">
                  {profile.photos.map((photo: any) => (
                    <div key={photo.id} className="aspect-square rounded-lg overflow-hidden">
                      <img
                        src={photo.photo_url}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Content Preview */}
            {currentStage < 3 && stageInfo.next_unlocks && stageInfo.next_unlocks.length > 0 && (
              <div className="bg-base-200 rounded-lg p-4 text-center mt-6">
                <p className="text-sm text-base-content/70 mb-2">
                  🔒 {currentStage === 0 ? '대화를 시작하여' : '다음 단계로 진행하여'} 더 많은 정보를 확인하세요
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {stageInfo.next_unlocks.map((unlock: string, idx: number) => (
                    <span key={idx} className="badge badge-ghost badge-sm">
                      {unlock}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 뒤로가기 버튼 */}
        <div className="mt-6">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-outline btn-block"
          >
            {t('publicProfile.backButton')}
          </button>
        </div>
      </div>
    </Layout>
  )
}
